<script lang="ts">
export default { name: 'DatePickerCalendar' };
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ChevronLeftIcon, ChevronRightIcon } from '@lucide/vue';
import { CalendarDate, today, getLocalTimeZone } from '@internationalized/date';
import {
  CalendarRoot,
  CalendarHeader,
  CalendarHeading,
  CalendarPrev,
  CalendarNext,
  CalendarGrid,
  CalendarGridHead,
  CalendarGridBody,
  CalendarGridRow,
  CalendarHeadCell,
  CalendarCell,
  CalendarCellTrigger,
} from 'reka-ui';
import type { DateValue } from 'reka-ui';

const props = defineProps<{
  modelValue: CalendarDate | undefined;
  minDate?: CalendarDate;
  maxDate?: CalendarDate;
}>();

const emit = defineEmits<{
  select: [value: CalendarDate];
}>();

type View = 'day' | 'month' | 'year';
const view = ref<View>('day');

const placeholder = ref<CalendarDate>(props.modelValue ?? today(getLocalTimeZone()));

watch(
  () => props.modelValue,
  (v) => {
    // Sync the visible month when the parent provides a value.
    // When the parent clears (undefined), intentionally hold the current placeholder
    // so the calendar stays where the user was navigating.
    if (v) placeholder.value = v;
  },
);

const calendarValue = computed(() => props.modelValue as DateValue | undefined);
const minValue = computed(() => props.minDate as DateValue | undefined);
const maxValue = computed(() => props.maxDate as DateValue | undefined);
const placeholderValue = computed({
  get() {
    return placeholder.value as DateValue;
  },
  set(v: DateValue) {
    // CalendarRoot writes back the same CalendarDate shape; cast is safe.
    placeholder.value = v as CalendarDate;
  },
});

function onCalendarChange(v: DateValue | undefined) {
  if (v) emit('select', v as CalendarDate);
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function openMonthView() {
  view.value = 'month';
}
function pickMonth(monthIndex0: number) {
  placeholder.value = new CalendarDate(placeholder.value.year, monthIndex0 + 1, 1);
  view.value = 'day';
}

function monthDisabled(monthIndex0: number): boolean {
  const year = placeholder.value.year;
  const monthStart = new CalendarDate(year, monthIndex0 + 1, 1);
  const lastDay = monthStart.calendar.getDaysInMonth(monthStart);
  const monthEnd = new CalendarDate(year, monthIndex0 + 1, lastDay);
  if (props.minDate && monthEnd.compare(props.minDate) < 0) return true;
  if (props.maxDate && monthStart.compare(props.maxDate) > 0) return true;
  return false;
}

function yearDisabled(year: number): boolean {
  const jan1 = new CalendarDate(year, 1, 1);
  const dec31 = new CalendarDate(year, 12, 31);
  if (props.minDate && dec31.compare(props.minDate) < 0) return true;
  if (props.maxDate && jan1.compare(props.maxDate) > 0) return true;
  return false;
}

const monthHeaderLabel = computed(() => String(placeholder.value.year));

function stepMonthViewYear(delta: number) {
  placeholder.value = new CalendarDate(placeholder.value.year + delta, placeholder.value.month, 1);
}

function yearGridStart(): number {
  const y = placeholder.value.year;
  return y - ((y - 1) % 12);
}
const yearCells = computed<number[]>(() => {
  const start = yearGridStart();
  return Array.from({ length: 12 }, (_, i) => start + i);
});
const yearHeaderLabel = computed(() => {
  const start = yearGridStart();
  return `${start} – ${start + 11}`;
});
function pickYear(year: number) {
  placeholder.value = new CalendarDate(year, placeholder.value.month, 1);
  view.value = 'month';
}
function stepYearGrid(delta: number) {
  placeholder.value = new CalendarDate(placeholder.value.year + delta * 12, placeholder.value.month, 1);
}
</script>

<template>
  <!-- Fixed width + min-height so the calendar bounding rect is constant across
       months (5- vs 6-week) and across views (day / month / year). FloatingUI
       computes the popover position once on open; without a stable size, content
       changes would re-trigger positioning and the calendar would jitter as the
       user clicks ‹ / › or drills views. -->
  <div class="p-4 select-none w-72 min-h-[19rem]">
    <!-- Day view -->
    <CalendarRoot
      v-if="view === 'day'"
      v-slot="{ weekDays, grid }"
      :model-value="calendarValue"
      v-model:placeholder="placeholderValue"
      :min-value="minValue"
      :max-value="maxValue"
      @update:model-value="onCalendarChange">
      <CalendarHeader class="flex items-center justify-between">
        <CalendarPrev
          aria-label="Previous month"
          class="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100">
          <ChevronLeftIcon :size="17" />
        </CalendarPrev>
        <CalendarHeading v-slot="{ headingValue }">
          <button
            type="button"
            data-test="dpc-title"
            aria-label="Switch to month view"
            class="rounded-md px-2 py-1 font-medium text-gray-900 hover:bg-gray-100"
            @click="openMonthView">
            {{ headingValue }} &#9662;
          </button>
        </CalendarHeading>
        <CalendarNext
          aria-label="Next month"
          class="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100">
          <ChevronRightIcon :size="17" />
        </CalendarNext>
      </CalendarHeader>
      <div class="flex flex-col space-y-4 pt-4">
        <CalendarGrid
          v-for="month in grid"
          :key="month.value.toString()"
          class="w-full border-collapse">
          <CalendarGridHead>
            <CalendarGridRow class="mb-1 flex w-full justify-between">
              <CalendarHeadCell
                v-for="day in weekDays"
                :key="day"
                class="w-8 text-xs font-medium text-gray-600">
                {{ day }}
              </CalendarHeadCell>
            </CalendarGridRow>
          </CalendarGridHead>
          <CalendarGridBody>
            <CalendarGridRow
              v-for="(weekDates, i) in month.rows"
              :key="`row-${i}`"
              class="flex w-full">
              <CalendarCell
                v-for="weekDate in weekDates"
                :key="weekDate.toString()"
                :date="weekDate">
                <CalendarCellTrigger
                  :day="weekDate"
                  :month="month.value"
                  data-test="dpc-day-cell"
                  class="data-selected:bg-primary data-selected:text-white data-today:ring data-today:ring-primary/50 data-disabled:opacity-40 data-outside-view:text-gray-300 relative flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-sm text-gray-900 hover:border-gray-300 hover:bg-gray-50" />
              </CalendarCell>
            </CalendarGridRow>
          </CalendarGridBody>
        </CalendarGrid>
      </div>
    </CalendarRoot>

    <!-- Month view -->
    <div v-else-if="view === 'month'">
      <div class="flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous year"
          class="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
          @click="stepMonthViewYear(-1)">
          <ChevronLeftIcon :size="17" />
        </button>
        <button
          type="button"
          data-test="dpc-title"
          aria-label="Switch to year view"
          class="rounded-md px-2 py-1 font-medium text-gray-900 hover:bg-gray-100"
          @click="view = 'year'">
          {{ monthHeaderLabel }} &#9662;
        </button>
        <button
          type="button"
          aria-label="Next year"
          class="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
          @click="stepMonthViewYear(1)">
          <ChevronRightIcon :size="17" />
        </button>
      </div>
      <div class="grid grid-cols-4 gap-2 pt-4">
        <button
          v-for="(label, i) in MONTHS_SHORT"
          :key="label"
          type="button"
          data-test="dpc-month-cell"
          :disabled="monthDisabled(i)"
          class="rounded-md bg-gray-50 px-2 py-3 text-sm text-gray-900 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-50"
          @click="pickMonth(i)">
          {{ label }}
        </button>
      </div>
    </div>

    <!-- Year view -->
    <div v-else-if="view === 'year'">
      <div class="flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous 12 years"
          class="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
          @click="stepYearGrid(-1)">
          <ChevronLeftIcon :size="17" />
        </button>
        <span data-test="dpc-title" class="font-medium text-gray-900 px-2 py-1">
          {{ yearHeaderLabel }}
        </span>
        <button
          type="button"
          aria-label="Next 12 years"
          class="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
          @click="stepYearGrid(1)">
          <ChevronRightIcon :size="17" />
        </button>
      </div>
      <div class="grid grid-cols-4 gap-2 pt-4">
        <button
          v-for="y in yearCells"
          :key="y"
          type="button"
          data-test="dpc-year-cell"
          :disabled="yearDisabled(y)"
          class="rounded-md bg-gray-50 px-2 py-3 text-sm text-gray-900 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-50"
          @click="pickYear(y)">
          {{ y }}
        </button>
      </div>
    </div>
  </div>
</template>
